import { CaretRightIcon } from '@panwds/icons';
import { classNames, twMerge } from '@panwds/tailwindcss';
import { config } from '@react-spring/core';
import { a, useSpring } from '@react-spring/web';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import useMeasure from 'react-use-measure';
import { v4 as uuidV4 } from 'uuid';
import { Title } from '../Typography/index.js';
import { computeBorderClasses, computeTitleBorderClasses } from './accordion.logic.js';
/**
 * A collapsible panel
 */
export function AccordionPanel({ canClose = true, children, icon, isOpen = true, level = 4, noPadding = false, indicator = true, onClick, title, caption, as: Component = 'li', addClassName = '', addTitleClassName = '', appearance = 'default' }) {
    const [buttonId, bodyId] = useMemo(() => {
        const baseId = uuidV4();
        return [`${baseId}-accordion-button`, `${baseId}-accordion-body`];
    }, []);
    const outerRef = useRef(null);
    // assert to any due to old type definition
    const [innerRef, bounds] = useMeasure();
    // const [isAnimatingHeight, setIsAnimatingHeight] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState(false);
    const { height, ...style } = useSpring({
        immediate: !isMounted,
        config: { ...config.stiff, clamp: true },
        from: {
            height: 0,
            opacity: 0,
            translateY: '-100%'
        },
        to: {
            height: bounds.height,
            opacity: 1,
            translateY: '0%'
        },
        // onRest: {
        //   height:
        //     /* istanbul ignore next - animations are skipped in tests */ () => {
        // setIsAnimatingHeight(false);
        //     },
        // },
        onStart: {
            height: /* istanbul ignore next - animations are skipped in tests */ () => {
                setIsMounted(true);
                // setIsAnimatingHeight(!animation.finished);
            }
        },
        reverse: !isOpen
    });
    const iconClasses = classNames('twds-flex twds-flex-initial', caption ? 'twds-mt-0.5 twds-py-1' : '', !indicator && 'twds-ml-2');
    const isCard = appearance === 'card';
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.(event);
        }
    }, [onClick]);
    return (React.createElement(Component, { "data-testid": "accordion-panel", className: twMerge('twds-flex twds-flex-col twds-bg-white dark:twds-bg-gray-1000', computeBorderClasses(appearance), isCard && 'twds-my-1', addClassName) },
        React.createElement(Title, { addClassName: twMerge('twds-w-full', computeTitleBorderClasses(appearance), (canClose || !isOpen) && 'hover:twds-bg-blue-200 dark:hover:twds-bg-gray-800', isOpen && 'hover:twds-rounded-b-none', addTitleClassName), level: level, size: "xs" },
            React.createElement("div", { "aria-controls": bodyId, "aria-disabled": isOpen && !canClose, "aria-expanded": isOpen, className: classNames('twds-box-border twds-border-0 twds-bg-transparent twds-font-lato twds-font-semibold twds-flex twds-w-full twds-pl-2 twds-pr-2 twds-py-2 twds-space-x-1 focus-visible:twds-ring', isOpen && !canClose ? 'twds-cursor-default' : 'twds-cursor-pointer', caption ? 'twds-items-start' : 'twds-items-center'), id: buttonId, onClick: onClick, onKeyDown: handleKeyDown, role: "button", tabIndex: 0 },
                indicator && (React.createElement("span", { className: "twds-flex twds-flex-initial twds-mx-2 twds-py-1.5" },
                    React.createElement(CaretRightIcon, { "data-testid": "accordion-panel-indicator", className: classNames('twds-text-gray-600 dark:twds-text-gray-400 twds-transition twds-ease-in-out twds-duration-300 twds-origin-center', isOpen ? 'twds-rotate-90' : 'twds-rotate-0'), size: "xs" }))),
                icon && React.createElement("div", { className: iconClasses }, icon),
                React.createElement("span", { className: classNames('twds-font-lato twds-flex-grow twds-text-left twds-text-base twds-font-semibold twds-text-default dark:twds-text-gray-100', !indicator && !icon && 'twds-ml-2') },
                    title,
                    React.createElement("p", { className: "twds-p-0 twds-m-0 twds-font-normal twds-text-xs twds-text-gray-600 dark:twds-text-gray-300" }, caption)))),
        React.createElement(a.div, { className: classNames(
            // (isOpen || isAnimatingHeight) && "twds-border",
            'twds-border-t-0 twds-overflow-hidden twds-border-gray-300 twds-will-change-transform', 'dark:twds-border-gray-950'), ref: outerRef, style: { height } },
            React.createElement(a.div, { "aria-labelledby": buttonId, className: classNames(!noPadding && 'twds-p-4 twds-pl-6', 'twds-inset-x-4 twds-relative'), id: bodyId, ref: innerRef, style: style }, children))));
}
//# sourceMappingURL=AccordionPanel.js.map